import React from 'react';
import { Sprout, Handshake, Wrench, DollarSign, TreePine } from 'lucide-react';
import Hero from '@/components/Hero';
import SectionHeader from '@/components/SectionHeader';
import Reveal from '@/components/Reveal';
import GrowerApplicationForm from '@/components/forms/GrowerApplicationForm';
import VideoFeature from '@/components/VideoFeature';
import Seo from '@/components/Seo';
import { STOCK } from '@/lib/constants';

const SERVICES = [
  { icon: Sprout, accent: 'bg-[hsl(var(--c-leaf))]', title: 'Harvest assistance', body: 'We coordinate harvests with experienced pickers — saving you the ladder work and reducing fruit waste.' },
  { icon: DollarSign, accent: 'bg-[hsl(var(--c-mango-yellow))]', title: 'Fruit purchasing', body: 'We buy quality mangoes from your trees at fair, transparent rates.' },
  { icon: Wrench, accent: 'bg-[hsl(var(--c-teal))]', title: 'Tree maintenance referrals', body: 'We can refer trusted arborists for pruning, fertilization, and tree health.' },
  { icon: TreePine, accent: 'bg-[hsl(var(--c-mango-orange))]', title: 'Tree cleanup', body: 'Post-harvest cleanup so your yard or grove is left in great shape.' },
  { icon: Handshake, accent: 'bg-[hsl(var(--c-mango-red))]', title: 'Long-term partnerships', body: 'Multi-season grower partnerships with reliable harvest scheduling.' },
];

export default function Growers() {
  return (
    <div data-testid="growers-page">
      <Seo
        title="For Growers — Harvest Your Florida Mango Trees"
        description="Have mango trees? Florida SunKissed Mangoes works with homeowners and growers across South Florida to harvest, purchase, and connect your fruit to real markets."
        keywords="sell mangoes, mango tree harvest, Florida mango grower, fruit purchasing, backyard mango income"
        ogImage={STOCK.mango_tree}
      />

      <Hero
        testId="growers-hero"
        eyebrow="For Growers"
        title={<>Have mango trees? <br className="hidden sm:block" /> <span className="text-[hsl(var(--c-mango-yellow))]">Let’s harvest them.</span></>}
        subtitle="Florida SunKissed Mangoes works with homeowners, property managers, growers, and agricultural partners throughout South Florida — turning seasonal fruit into income, reducing waste, and connecting your harvest to real markets."
        block="leaf"
        bgImage={STOCK.mango_tree}
        rightImage={STOCK.golden_orchard}
        imageBlock="yellow"
        rightAccent="red"
        leftAccent="orange"
        ctas={[
          { label: 'Apply Now', href: '#grower-form-section', variant: 'yellow', testId: 'growers-hero-apply' },
          { label: 'Talk To Us', to: '/contact?subject=Grower+Question', variant: 'orange', testId: 'growers-hero-talk' },
        ]}
      />

      <section className="block-cream border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              eyebrow="What We Offer"
              size="xl"
              title={<>Real partnership for <br className="hidden sm:block" /> South Florida growers.</>}
              subtitle="Whether you have a single backyard tree or a working grove, we have a partnership option that fits."
            />
          </Reveal>
          <div className="mt-12 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-[hsl(var(--c-ink))]">
            {SERVICES.map(({ icon: Icon, title, body, accent }, i) => (
              <Reveal key={title} delay={i * 0.05}>
                <div className="bg-white p-7 sm:p-8 h-full border-r-2 border-b-2 border-[hsl(var(--c-ink))] last:border-r-0 sm:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r-2 lg:[&:nth-child(3n)]:border-r-0 lift-on-hover" data-testid={`growers-service-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <div className={`inline-flex h-12 w-12 items-center justify-center ${accent} border-2 border-[hsl(var(--c-ink))] text-white`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-2xl font-extrabold">{title}</h3>
                  <p className="mt-2 text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GROWERS FEATURE STRIP */}
      <section aria-hidden="true" className="relative h-[260px] sm:h-[320px] lg:h-[400px] border-t-2 border-b-2 border-[hsl(var(--c-ink))] overflow-hidden">
        <img src={STOCK.mango_grove} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--c-leaf-deep))]/75 via-[hsl(var(--c-leaf-deep))]/35 to-transparent" />
        <div className="absolute inset-0 grain" />
        <div className="relative mx-auto max-w-[1400px] h-full px-5 sm:px-10 lg:px-16 flex items-center">
          <div className="max-w-2xl">
            <div className="eyebrow text-white/90">From Backyards & Groves</div>
            <h3 className="mt-3 font-heading display-md font-extrabold text-white leading-[0.98]">
              Turning South Florida's <span className="text-[hsl(var(--c-mango-yellow))]">mango bounty</span> into income.
            </h3>
          </div>
        </div>
        <div className="absolute top-0 left-0 right-0 h-[5px] bg-[hsl(var(--c-mango-yellow))]" />
        <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-[hsl(var(--c-mango-orange))]" />
      </section>

      {/* FEATURE VIDEO — aerial grove */}
      <VideoFeature
        testId="growers-video"
        src={STOCK.video_grove}
        poster={STOCK.video_grove_poster}
        tone="forest"
        eyebrow="A Day In The Grove"
        title={<>Real Florida groves. <span className="text-grad-mango">Real harvests.</span></>}
        subtitle="A peek inside the South Florida groves we partner with — from canopy to crate."
      />

      <section id="grower-form-section" data-testid="grower-form-section" className="block-orange border-t-2 border-[hsl(var(--c-ink))] scroll-mt-32">
        <div className="mx-auto max-w-[1000px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              tone="cream"
              eyebrow="Schedule A Harvest Assessment"
              size="xl"
              title="Become a grower partner."
              subtitle="Tell us about your trees. We’ll follow up to walk you through harvest options and what your fruit may be worth."
            />
          </Reveal>
          <Reveal variant="scale">
            <div className="mt-12 bg-white border-2 border-[hsl(var(--c-ink))] p-6 sm:p-10">
              <GrowerApplicationForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
